import { HeatTier, HEAT_TIER_THRESHOLDS } from '@bracketblitz/shared-types'
import { useGamingStore } from '@/stores/gamingStore'
import { getHeatTierColor } from '@bracketblitz/game-engine'

interface HeatMeterProps {
  mode?: 'compact' | 'expanded'
  className?: string
}

const TIER_COLORS = {
  cold: 'from-slate-600 to-slate-700',
  warm: 'from-amber-500 to-orange-500',
  hot: 'from-orange-500 to-red-500',
  on_fire: 'from-red-500 to-red-600',
  blazing: 'from-blue-500 to-purple-600'
} as const

const TIER_NAMES = {
  cold: 'Cold',
  warm: 'Warm',
  hot: 'Hot',
  on_fire: 'On Fire',
  blazing: 'Blazing'
} as const

const TIER_EMOJIS = {
  cold: '🧊',
  warm: '🔥',
  hot: '🔥🔥',
  on_fire: '🔥🔥🔥',
  blazing: '🔥🔥🔥🔥'
} as const

export function HeatMeter({ mode = 'expanded', className = '' }: HeatMeterProps) {
  const heatMeter = useGamingStore(state => state.heatMeter)
  
  if (!heatMeter) return null

  const { currentStreak, currentTier, multiplier, progress } = heatMeter
  const isCompact = mode === 'compact'
  
  // Calculate next tier info
  const tiers: HeatTier[] = ['cold', 'warm', 'hot', 'on_fire', 'blazing']
  const currentIndex = tiers.indexOf(currentTier)
  const isMaxTier = currentIndex >= tiers.length - 1
  const nextTier = !isMaxTier ? tiers[currentIndex + 1] : null
  const nextThreshold = nextTier ? HEAT_TIER_THRESHOLDS[nextTier].minStreak : null

  return (
    <div
      className={`bg-slate-900 border border-slate-700 rounded-lg ${isCompact ? 'p-3' : 'p-6'} ${className}`}
    >
      {/* Header */}
      <div className={`flex items-center justify-between ${isCompact ? 'mb-2' : 'mb-4'}`}>
        <h3 className={`font-bold text-white ${isCompact ? 'text-sm' : 'text-lg'}`}>
          Heat Meter
        </h3>
        <div
          className={`flex items-center gap-2 ${isCompact ? 'text-xs' : 'text-sm'}`}
        >
          <span className="text-gray-300">{TIER_EMOJIS[currentTier]}</span>
          <span className="text-white font-semibold">
            {TIER_NAMES[currentTier]}
          </span>
          <span className="text-gray-400">
            {multiplier.toFixed(1)}x
          </span>
        </div>
      </div>

      {/* Heat Meter Bar */}
      <div className={`relative ${isCompact ? 'h-3' : 'h-6'} bg-slate-800 rounded-full overflow-hidden`}>
        <div
          className={`absolute inset-0 bg-gradient-to-r ${TIER_COLORS[currentTier]} rounded-full`}
        />
        
        {/* Fire particles for On Fire/Blazing */}
{(currentTier === 'on_fire' || currentTier === 'blazing') && (
            <div
              className="absolute inset-0 pointer-events-none"
            >
              {[...Array(currentTier === 'blazing' ? 8 : 5)].map((_, i) => (
                <div
                  key={i}
                  className={`absolute w-1 h-1 ${
                    currentTier === 'blazing' ? 'bg-blue-400' : 'bg-orange-400'
                  } rounded-full`}
                  style={{
                    left: `${15 + (i * 10)}%`,
                    top: '25%',
                  }}
                />
              ))}
            </div>
          )}
{/* Glow effect */}
        {(currentTier === 'on_fire' || currentTier === 'blazing') && (
          <div
            className={`absolute inset-0 bg-gradient-to-r ${TIER_COLORS[currentTier]} rounded-full blur-sm opacity-50`}
          />
        )}
      </div>

      {/* Stats */}
      {!isCompact && (
        <div className="flex justify-between items-center mt-4 text-sm">
          <div className="text-center">
            <div className="text-2xl font-bold text-white">{currentStreak}</div>
            <div className="text-gray-400">Streak</div>
          </div>
          
          <div className="text-center">
            <div className="text-xl font-bold text-green-400">
              {multiplier.toFixed(2)}x
            </div>
            <div className="text-gray-400">Multiplier</div>
          </div>
          
          {nextThreshold && (
            <div className="text-center">
              <div className="text-lg font-bold text-blue-400">
                {nextThreshold - currentStreak}
              </div>
              <div className="text-gray-400">To {TIER_NAMES[nextTier!]}</div>
            </div>
          )}
        </div>
      )}

      {/* Compact mode streak display */}
      {isCompact && (
        <div className="flex justify-between items-center mt-2 text-xs">
          <span className="text-white font-semibold">{currentStreak} streak</span>
          {nextThreshold && (
            <span className="text-gray-400">
              {nextThreshold - currentStreak} to {TIER_NAMES[nextTier!]}
            </span>
          )}
        </div>
      )}

      {/* Tier indicators */}
      {!isCompact && (
        <div className="flex justify-between mt-4">
          {tiers.map((tier, index) => (
            <div
              key={tier}
              className={`w-8 h-8 rounded-full border-2 flex items-center justify-center text-xs
                ${index <= currentIndex ? 
                  `border-${getHeatTierColor(tier).slice(1)} bg-gradient-to-r ${TIER_COLORS[tier]}` : 
                  'border-slate-600 bg-slate-800'
                }
              `}
            >
              {HEAT_TIER_THRESHOLDS[tier].minStreak}
            </div>
          ))}
        </div>
      )}

      {/* Blazing special effect */}
{currentTier === 'blazing' && !isCompact && (
          <div
            className="absolute -inset-2 bg-gradient-to-r from-blue-500/20 to-purple-600/20 rounded-lg -z-10"
          />
        )}
</div>
  )
}

export default HeatMeter